import { useId, useState } from "react";
import {
  ONBOARDING_WAITING_ON_LABEL,
  type OnboardingStatus,
  type OnboardingTimelineState,
  type OnboardingTimelineStep,
} from "@care-by-ella/contracts";
import { PatientGroupBadge } from "./PatientGroupBadge";

// TES-1117 (Emelie 2026-10-05) — the precise onboarding status. The words
// come from the server (deriveOnboardingStatus), so the patient list and the
// overview card cannot disagree about where a patient stands.

function dateSv(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE");
}

/** The patient list's "Aktuell status" cell. */
export function OnboardingStatusCell({
  status,
}: {
  status: OnboardingStatus | undefined;
}) {
  if (!status) return <span className="text-textSecondary">—</span>;
  return (
    <div className="flex flex-col">
      {status.sharedStatus ? (
        <span className="text-xs text-textSecondary">
          {status.sharedStatus}
        </span>
      ) : null}
      <span>{status.label}</span>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-[2px]">
      <dt className="text-xs uppercase tracking-wide text-textSecondary">
        {label}
      </dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

/**
 * The overview's onboarding card: journey, current step, next step, latest
 * relevant activity with its date, and who we are waiting on. Nothing is
 * rendered once onboarding is over (started, declined, paused, ended); the
 * header's clinical stage carries her from there.
 */
export function OnboardingStatusCard({
  status,
  timeline,
  initiallyOpen = false,
}: {
  status: OnboardingStatus | undefined;
  /** TES-1117 S2 — the steps the card opens; absent from an older API. */
  timeline?: OnboardingTimelineStep[];
  /** For the render test. */
  initiallyOpen?: boolean;
}) {
  const [open, setOpen] = useState(initiallyOpen);
  const timelineId = useId();
  if (!status || !status.inOnboarding) return null;
  return (
    <section
      aria-label="Onboarding"
      className="rounded-md border border-brand/40 bg-surface p-md"
    >
      <div className="mb-sm flex items-center gap-sm">
        <h2 className="font-display text-lg">Onboarding</h2>
        <PatientGroupBadge group={status.journey} />
      </div>
      <p className="text-xs uppercase tracking-wide text-textSecondary">
        Aktuell status
      </p>
      <p className="mb-md text-base font-medium">
        {status.sharedStatus ?? status.label}
        {status.detail ? (
          <span className="block text-sm font-normal text-textSecondary">
            {status.detail}
          </span>
        ) : null}
      </p>
      <dl className="grid grid-cols-1 gap-md sm:grid-cols-3">
        <Field label="Nästa steg">{status.nextStep ?? "—"}</Field>
        <Field label="Väntar på">
          {status.waitingOn
            ? ONBOARDING_WAITING_ON_LABEL[status.waitingOn]
            : "—"}
        </Field>
        <Field label="Senaste aktivitet">
          {status.latestActivity
            ? `${status.latestActivity.label}, ${dateSv(status.latestActivity.at)}`
            : "—"}
        </Field>
      </dl>
      {timeline && timeline.length > 0 ? (
        <div className="mt-md">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={timelineId}
            onClick={() => setOpen((o) => !o)}
            className="text-sm text-brand hover:underline"
          >
            {open ? "Dölj tidslinje" : "Visa tidslinje"}
          </button>
          {open ? (
            <OnboardingTimeline id={timelineId} steps={timeline} />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

// TES-1117 S2: proposed, from Emelie's own "inte påbörjad" / "pågår" /
// "klar" (decision 3).
const STATE_LABEL: Record<OnboardingTimelineState, string> = {
  done: "Klar",
  in_progress: "Pågår",
  pending: "Inte påbörjad",
};

const STATE_DOT: Record<OnboardingTimelineState, string> = {
  done: "bg-brand",
  in_progress: "bg-warning",
  pending: "border border-textSecondary bg-surface",
};

/**
 * The card's timeline: every step of her journey in the journey's order,
 * with its state and date. A step not reached yet has no date. Exported for
 * the render test.
 */
export function OnboardingTimeline({
  id,
  steps,
}: {
  id?: string;
  steps: OnboardingTimelineStep[];
}) {
  return (
    <ol id={id} aria-label="Tidslinje" className="mt-sm flex flex-col">
      {steps.map((s) => (
        <li
          key={s.key}
          data-step={s.key}
          data-state={s.state}
          className="flex items-start gap-sm border-t border-surfaceMuted py-xs text-sm"
        >
          <span
            aria-hidden
            className={`mt-[6px] h-[8px] w-[8px] shrink-0 rounded-full ${STATE_DOT[s.state]}`}
          />
          <span
            className={`flex-1 ${s.state === "pending" ? "text-textSecondary" : ""}`}
          >
            {s.label}
            {s.note ? (
              <span className="text-textSecondary"> · {s.note}</span>
            ) : null}
          </span>
          <span className="w-[7rem] text-textSecondary">
            {STATE_LABEL[s.state]}
          </span>
          <span className="w-[6rem] text-right tabular-nums text-textSecondary">
            {s.at ? dateSv(s.at) : "—"}
            {s.startedAt && s.startedAt !== s.at ? (
              <span className="block text-xs">
                påbörjad {dateSv(s.startedAt)}
              </span>
            ) : null}
          </span>
        </li>
      ))}
    </ol>
  );
}
